// src/components/dentally/DentallyAppointmentBadge.jsx
//
// Small provider badge for one appointment row: "CRM only" or the Dentally
// creation/cancellation state the API exposes today. Renders nothing unless
// the route practice has an active Dentally integration with view access
// (useDentallyIntegrationScope). Never labels a booking "Synced".
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '@mui/material';
import Iconify from '../Iconify';
import { useSelector } from '../../redux/store';
import useDentallyIntegrationScope from '../../hooks/useDentallyIntegrationScope';
import { TONE_COLOURS } from '../../utils/dentallyPatientLinkStatus';
import { getDentallyAppointmentPresentation, getDentallyAppointmentReference } from '../../utils/dentallyAppointmentStatus';

export default function DentallyAppointmentBadge({ dentally, practiceId, sx }) {
  const routePracticeId = useSelector((state) => state.practiceTreatmentList?.practiceId);
  const scope = useDentallyIntegrationScope(practiceId ?? routePracticeId);
  if (!scope.visible) return null;

  const presentation = getDentallyAppointmentPresentation(dentally);
  const colours = TONE_COLOURS[presentation.tone];
  const reference = getDentallyAppointmentReference(dentally);
  const tooltip = reference ? `${presentation.tooltip} ${reference}.` : presentation.tooltip;

  return (
    <Tooltip title={tooltip} placement="bottom">
      <Chip
        size="small"
        data-testid="dentally-appointment-badge"
        data-state={presentation.key}
        icon={<Iconify icon={presentation.icon} width={14} height={14} />}
        label={presentation.label}
        sx={{
          height: 22, fontSize: 11, fontWeight: 600, bgcolor: colours.bg, color: colours.fg,
          '& .MuiChip-icon': { color: colours.fg, ml: '6px' }, '& .MuiChip-label': { px: '6px' }, ...sx,
        }}
      />
    </Tooltip>
  );
}

DentallyAppointmentBadge.propTypes = {
  dentally: PropTypes.shape({
    appointment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creation_state: PropTypes.string,
    cancellation_state: PropTypes.string,
    reconciliation_required: PropTypes.bool,
  }),
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
};

DentallyAppointmentBadge.defaultProps = { dentally: null, practiceId: undefined, sx: undefined };
